import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, MobileHeader, TypeBadge, QueryState, CardGridSkeleton } from '@/components';
import { useCourses } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';
import type { CourseType } from '@/types/domain';

type Filter = 'all' | CourseType;

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'online', label: 'Online' },
  { id: 'onsite', label: 'Onsite' },
  { id: 'hybrid', label: 'Hybrid' },
];

export function MobileCatalog() {
  const navigate = useNavigate();
  const { L } = useLocalized();
  const coursesQuery = useCourses();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const list = useMemo(
    () =>
      (coursesQuery.data ?? [])
        .filter((c) => c.status === 'active')
        .filter((c) => filter === 'all' || c.type === filter)
        .filter((c) => !q || `${c.title.en}${c.title.th}${c.host}`.toLowerCase().includes(q.toLowerCase())),
    [coursesQuery.data, filter, q],
  );

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader title="Catalog" th="คอร์สเรียนทั้งหมด" />
      <div style={{ padding: '16px 20px 8px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 11, padding: '11px 14px', marginBottom: 12 }}>
          <Icon name="search" size={18} style={{ color: 'var(--ink-3)' }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search courses · ค้นหา"
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 15, width: '100%', fontFamily: 'var(--font)', color: 'var(--ink)' }}
          />
        </div>
        <div className="scroll" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                border: '1px solid',
                borderColor: filter === f.id ? 'var(--sage)' : 'var(--line-2)',
                background: filter === f.id ? 'var(--sage)' : 'var(--card)',
                color: filter === f.id ? 'var(--on-accent)' : 'var(--ink-2)',
                borderRadius: 99,
                padding: '7px 15px',
                fontSize: 13.5,
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: 'var(--font)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '12px 20px 28px' }}>
        <QueryState query={coursesQuery} skeleton={<CardGridSkeleton count={4} cols={1} />} isEmpty={() => list.length === 0} emptyIcon="search" emptyTitle="No courses match">
          {() => (
            <>
              {list.map((c) => (
                <button key={c.id} onClick={() => navigate(`/learn/catalog/${c.id}`)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
                  <div className="card" style={{ overflow: 'hidden' }}>
                    <div style={{ position: 'relative' }}>
                      {c.coverUrl ? (
                        <img src={c.coverUrl} alt={c.title.en} style={{ display: 'block', width: '100%', height: 'auto' }} />
                      ) : (
                        <div style={{ aspectRatio: '3 / 1', background: c.cover }} />
                      )}
                      <div style={{ position: 'absolute', left: 12, right: 12, bottom: 12, display: 'flex', justifyContent: 'space-between' }}>
                        <TypeBadge type={c.type} />
                        <span className="u-mono" style={{ fontSize: 11, color: 'var(--ink-2)', background: 'rgba(251,250,247,.8)', padding: '3px 7px', borderRadius: 6 }}>{c.code}</span>
                      </div>
                    </div>
                    <div style={{ padding: '13px 14px 15px' }}>
                      <div className="th" style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.35, marginBottom: 2 }}>{L(c.title)}</div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 11, lineHeight: 1.3 }}>{c.title.en}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--ink-3)' }}>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Icon name="bldg" size={14} /> {c.host}</span>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Icon name="clock" size={14} /> {c.hours}h</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
        </QueryState>
      </div>
    </div>
  );
}
